import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { EnvironmentProviders, NgModule, Provider, inject, provideAppInitializer } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';

import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';

import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { createSkyKickAngularProvider, SkyKickProvidersBuilder } from '@platform/dependency-injection';
import { CachingModule } from '@skykick/caching';
import { AppId, BannerModule, HeaderModule, IncludeCookiesInterceptor, LayoutModule, NavigationModule, UtilityModule } from '@skykick/core';
import { IPartnerCenterConnectionService, PartnerCenterActivationModule } from '@skykick/partner-center';
import { AbstractUserProvider, AuthModule } from '@sharedservices/identity-auth-angular';

import { localPlatformServicesStubProvider } from '../environments/local/platform-services.interceptor';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { PartnerCenterConnectionService } from './pci/infrastructure/partner-center-connection.service';
import { windowProvider, WindowToken } from './window';

export function getRoutingDomain() {
    const currentHost = globalThis.location.hostname;

    if (currentHost.includes('localhost') || currentHost.includes('-dev')) {
        return 'https://shared-dev-routing.cloudservices.connectwise.dev';
    } else if (currentHost.includes('-qa')) {
        return 'https://shared-qa-routing.cloudservices.connectwise.dev';
    }

    return 'https://routing.cloudservices.connectwise.com';
}

export function getLoginProviderDomain(hostname: string = globalThis.location.hostname) {
    const currentHost = hostname;

    if (currentHost.includes('-dev') || currentHost.includes('-sandbox') || currentHost.includes('localhost')) {
        return 'https://shared-dev-loginprovider.cloudservices.connectwise.dev';
    } else if (currentHost.includes('-preview')) {
        return 'https://shared-qa-loginprovider.cloudservices.connectwise.dev';
    }

    return 'https://loginprovider.cloudservices.connectwise.com';
}

export function LanguageLoader(translate: TranslateService, cookieService: CookieService): () => Promise<void> {
    const promise = new Promise<void>(resolve => {
        const cookieLang = cookieService.get('skLanguageCode');
        const browserLang = cookieLang || translate.getBrowserLang();
        const lang = /en|ja|sq|de/.exec(browserLang) ? browserLang : 'en';
        translate.use(lang);
        resolve();
    });
    return async () => promise;
}

export function TranslationLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

const providers: (EnvironmentProviders | Provider)[] = [
    ...new SkyKickProvidersBuilder()
        .add(createSkyKickAngularProvider(environment.userProvider, AbstractUserProvider, { alreadyExists: environment.production }))
        .build(),
    { provide: WindowToken, useFactory: windowProvider },
    CookieService,
    // InitializationService.run() is triggered from AppComponent.ngOnInit(), not from a
    // provideAppInitializer here: it calls AbstractUserProvider.getCurrentUser(), which (for
    // ConnectWise SSO) resolves asynchronously in AuthModule's own APP_INITIALIZER. Since
    // APP_INITIALIZERs run concurrently with no ordering guarantee between them, calling it here
    // would risk racing that. Angular only creates the root component (and runs its lifecycle
    // hooks) after every APP_INITIALIZER has resolved, so AppComponent.ngOnInit is safe.
    provideAppInitializer(() => {
        const initializerFn = (LanguageLoader)(inject(TranslateService), inject(CookieService));
        return initializerFn();
      }),
    {
        provide: HTTP_INTERCEPTORS,
        useClass: IncludeCookiesInterceptor,
        multi: true,
    }
];

export function PartnerCenterConnectionServiceFactory(http: HttpClient) {
    return new PartnerCenterConnectionService(http, globalThis as unknown as Window);
}

if (!environment.production) {
    providers.push(localPlatformServicesStubProvider);
}

@NgModule({
    declarations: [
        AppComponent
    ],
    exports: [
        RouterModule
    ],
    bootstrap: [AppComponent],
    imports: [
        BannerModule,
        AppRoutingModule,
        AuthModule.forRoot({
            LicenseAssignmentServiceBaseUrl: `${getRoutingDomain()}/pipe/${AppId.PlatformApis}/licenseassignment/api/v1`,
            SewrSystemDomain: getRoutingDomain(),
            LoginProviderBaseUrl: getLoginProviderDomain(),
            NoAccessPath: '/account-not-linked',
            PublicPaths: [],
            EnableSessionExpiredInterceptor: environment.production,
        }),
        NavigationModule,
        HeaderModule,
        LayoutModule,
        FormsModule,
        BrowserModule,
        UtilityModule.forRoot({
            sewrSystemDomain: getRoutingDomain()
        }),
        TranslateModule.forRoot({
            loader: { provide: TranslateLoader, useFactory: TranslationLoaderFactory, deps: [HttpClient] },
            isolate: true
        }),
        PartnerCenterActivationModule.forRoot({
            provide: IPartnerCenterConnectionService,
            useFactory: PartnerCenterConnectionServiceFactory,
            deps: [HttpClient]
        }),
        CachingModule
    ],
    providers: [
        ...providers,
        provideHttpClient(withInterceptorsFromDi())
    ]
})
export class AppModule { }
